import UAParser from "ua-parser-js";
import ActionCard from "src/components/blocks/ActionCard.js";
import { Box, Icon, Typography } from "@mui/material";
import Footer from "src/components/blocks/footers/Footer.js";

/**
 * Detect an embedded / in-app browser (Instagram, Facebook, TikTok, etc.) that
 * blocks OAuth handoff and webcal:// links, so the calendar sync silently fails.
 * @returns {{ isEmbedded: boolean, browserName: string, isAndroid: boolean, isTwitter: boolean }}
 */
export const detectEmbeddedBrowser = () => {
  const parserResults = new UAParser().getResult();
  const ua = parserResults.ua || "";
  const isAndroid = parserResults.os.name === "Android";
  const isIOS = parserResults.os.name === "iOS";
  const rawName = parserResults.browser.name || "";

  // Facebook's in-app WebView reports as "WebKit", not "Facebook", so match its
  // FBAN/FBAV/FB_IAB tokens on the raw UA.
  const facebookInApp = /(FBAN|FBAV|FB_IAB|FB4A|FBIOS)/i.test(ua);
  // Named in-app apps ua-parser recognizes directly.
  const namedApp = [
    "Instagram",
    "LinkedIn",
    "Snapchat",
    "Facebook",
    "TikTok",
    "Electron",
    "Twitter",
    "X",
  ].includes(rawName);
  // Generic WebViews carry no app token: an iOS WKWebView parses as "WebKit"
  // (real Safari is "Mobile Safari"; SFSafariViewController keeps the Safari
  // token too), and an Android WebView parses as "Chrome WebView" or carries a
  // ";wv" token. Both block Google OAuth (disallowed_useragent) + webcal handoff.
  const genericIosWebview = isIOS && rawName === "WebKit";
  const genericAndroidWebview = isAndroid && (rawName === "Chrome WebView" || /;\s*wv\b/i.test(ua));

  const isEmbeddedInsecure =
    namedApp ||
    facebookInApp ||
    ["inapp", "module", "mediaplayer"].includes(parserResults.browser.type) ||
    genericIosWebview ||
    genericAndroidWebview;
  const isTwitter = ["Twitter", "X"].includes(rawName);
  // Twitter/X on iOS hands OAuth off fine; only treat it as embedded on Android.
  const isEmbedded = isEmbeddedInsecure && !(isTwitter && !isAndroid);

  // Prefer the recognizable app name; blank for generic WebViews so the UI reads
  // "this in-app browser" rather than "WebKit's in-app browser".
  const browserName = facebookInApp ? "Facebook" : namedApp ? rawName : "";

  return { isEmbedded, browserName, isAndroid, isTwitter };
};

/**
 * Higher Order Component to check if the user agent is embedded in an insecure environment for OAuth
 */
export const userAgentEmbeddedCheck = (Children) => {
  const { isEmbedded, isTwitter, browserName } = detectEmbeddedBrowser();

  if (isEmbedded) {
    return () => {
      return (
        <>
          <style>
            {`
        @keyframes vertical-slide {
          0% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(16px);
          }
          100% {
            transform: translateY(0);
          }
        }
        `}
          </style>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              minHeight: "100vh",
            }}
          >
            <div>
              <Box
                sx={{
                  margin: "auto",
                }}
              >
                <ActionCard
                  title={
                    <Typography variant="h2">
                      {browserName} has changed its browser
                      policies.
                    </Typography>
                  }
                  description={
                    <Box sx={{ mt: 2 }}>
                      To continue signing in, tap the menu
                      <br />
                      {!isTwitter ? "above" : "below"} and select 'Open in
                      browser'.
                    </Box>
                  }
                  iconName="perm_contact_calendar"
                  sx={{
                    top: "50vh",
                    transform: "translate(-50%, -75%)",
                    minWidth: "250px",
                    maxWidth: "250px",
                  }}
                  iconSx={{
                    fontWeight: "200 !important",
                  }}
                />
              </Box>
            </div>
            <Footer />

            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                fontWeight: 300,
                fontSize: "2rem",
                position: "fixed",
                ...{
                  ...(!isTwitter
                    ? {
                        top: "10px",
                        right: "12px",
                      }
                    : {
                        bottom: "10px",
                        right: "12px",
                        rotate: "180deg",
                      }),
                },
                animation: "vertical-slide 1s infinite",
                zIndex: 1,
              }}
            >
              arrow_upward
            </Icon>
          </div>
        </>
      );
    };
  }

  return Children;
};
